import { useState, useEffect, useCallback, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Sparkles, Plus, Search, Sun, Moon, LogOut, Users, ZoomIn, ZoomOut, Maximize,
  CheckSquare, StickyNote, AlarmClock, MessageCircle, Bookmark, Radio, Shapes,
  LayoutGrid, Spline, X, Heart, Flame, Lightbulb, Wand2, Newspaper, Rss,
} from "lucide-react";
import { VIBES, MOODS } from "@/lib/vibesContent";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import CardShell from "@/components/CardShell";
import AdminPanel from "@/components/AdminPanel";
import WelcomeTour from "@/components/WelcomeTour";
import TemplatePicker from "@/components/TemplatePicker";
import { WhatsAppIcon } from "@/components/cards/WhatsAppCard";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const CARD_TYPES = [
  { type: "task", label: "Task", icon: CheckSquare, color: "mint", w: 340 },
  { type: "note", label: "Note", icon: StickyNote, color: "butter", w: 300 },
  { type: "reminder", label: "Reminder", icon: AlarmClock, color: "peach", w: 300 },
  { type: "chat", label: "Chat", icon: MessageCircle, color: "sky", w: 380 },
  { type: "bookmark", label: "Bookmarks", icon: Bookmark, color: "lavender", w: 340 },
  { type: "ping", label: "Ping", icon: Radio, color: "cyber_pink", w: 300 },
  { type: "whatsapp", label: "WhatsApp", icon: WhatsAppIcon, color: "mint", w: 380 },
  { type: "compliment", label: "Compliments", icon: Heart, color: "cyber_pink", w: 300 },
  { type: "motivation", label: "Motivation", icon: Flame, color: "butter", w: 320 },
  { type: "learning", label: "Learning", icon: Lightbulb, color: "sky", w: 340 },
  { type: "news", label: "News (India)", icon: Newspaper, color: "neo_slate", w: 360 },
  { type: "content", label: "Content Feed", icon: Rss, color: "lavender", w: 360 },
  { type: "custom", label: "Custom Card", icon: Shapes, color: "neo_slate", w: 320 },
];

const GREETINGS = ["let's make today iconic ✦", "small steps, big vibes 🚀", "your canvas awaits ✨", "stay cosmic 🌙"];

export default function Dashboard() {
  const { user, logout } = useAuth();
  const qc = useQueryClient();
  const [dark, setDark] = useState(() => localStorage.getItem("nebula-dark") === "1");
  const [search, setSearch] = useState("");
  const [adminOpen, setAdminOpen] = useState(false);
  const [view, setView] = useState({ x: 0, y: 0, zoom: 1 });
  const [pinging, setPinging] = useState(false);
  const [mode, setMode] = useState(() => localStorage.getItem("nebula-view") || "canvas");
  const [vibe, setVibe] = useState(() => localStorage.getItem("nebula-vibe") || "default");
  const [vibeOpen, setVibeOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(() => !localStorage.getItem("nexfy-tour-done"));
  const closeTour = () => { localStorage.setItem("nexfy-tour-done", "1"); setTourOpen(false); };
  const [templateOpen, setTemplateOpen] = useState(() => !!user?.pending_template);
  const finishTemplate = () => { setTemplateOpen(false); qc.invalidateQueries({ queryKey: ["cards"] }); };
  const [connect, setConnect] = useState({ active: false, source: null });
  const [livePos, setLivePos] = useState({});
  const cardRefs = useRef({});
  const panRef = useRef(null);
  const greeting = useRef(GREETINGS[Math.floor(Math.random() * GREETINGS.length)]).current;

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("nebula-dark", dark ? "1" : "0");
  }, [dark]);

  useEffect(() => localStorage.setItem("nebula-view", mode), [mode]);

  const currentVibe = VIBES.find((v) => v.id === vibe) || VIBES[0];
  const applyVibe = (v) => {
    setVibe(v.id);
    localStorage.setItem("nebula-vibe", v.id);
    setVibeOpen(false);
    toast(`${v.emoji} ${v.name} vibes on`);
  };

  const { data: cards = [] } = useQuery({
    queryKey: ["cards"],
    queryFn: () => api.get("/cards").then((r) => r.data),
  });
  const { data: users = [] } = useQuery({
    queryKey: ["users"],
    queryFn: () => api.get("/users").then((r) => r.data),
  });

  const { data: connectors = [] } = useQuery({
    queryKey: ["connectors"],
    queryFn: () => api.get("/connectors").then((r) => r.data),
  });

  const { data: online = [] } = useQuery({
    queryKey: ["presence"],
    queryFn: () => api.get("/presence").then((r) => r.data),
    refetchInterval: 30000,
  });

  const seenPings = useRef(new Set());
  const showPing = (p) => {
    if (seenPings.current.has(p._id)) return;
    seenPings.current.add(p._id);
    setPinging(true);
    setTimeout(() => setPinging(false), 4000);
    toast(`📣 ${p.from_name} pinged you`, { description: p.message, duration: 8000 });
    api.post(`/pings/${p._id}/read`);
  };
  const showPingRef = useRef(showPing);
  showPingRef.current = showPing;

  useQuery({
    queryKey: ["pings-unread"],
    queryFn: async () => {
      const { data } = await api.get("/pings/unread");
      data.forEach((p) => showPingRef.current(p));
      return data;
    },
    refetchInterval: 15000,
  });

  useEffect(() => {
    let ws, timer, alive = true;
    const openWs = () => {
      try {
        ws = new WebSocket(`${process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws")}/api/ws`);
        ws.onopen = () => qc.invalidateQueries({ queryKey: ["presence"] });
        ws.onmessage = (e) => {
          try {
            const msg = JSON.parse(e.data);
            if (msg.event === "message") qc.invalidateQueries({ queryKey: ["messages", msg.channel_id] });
            if (msg.event === "ping") showPingRef.current(msg.ping);
            if (msg.event === "wa_message") {
              qc.invalidateQueries({ queryKey: ["wa-chats"] });
              qc.invalidateQueries({ queryKey: ["wa-msgs", msg.jid] });
              toast(`💬 WhatsApp · ${msg.chat_name || msg.sender || "New message"}`, {
                description: msg.text, duration: 6000,
              });
            }
            if (msg.event === "presence") {
              qc.setQueryData(["presence"], (old = []) =>
                msg.online ? (old.includes(msg.user_id) ? old : [...old, msg.user_id]) : old.filter((id) => id !== msg.user_id));
            }
          } catch {}
        };
        ws.onclose = () => { if (alive) timer = setTimeout(openWs, 3000); };
      } catch {
        timer = setTimeout(openWs, 3000);
      }
    };
    openWs();
    return () => {
      alive = false;
      clearTimeout(timer);
      try {
        if (ws) { ws.onclose = null; if (ws.readyState === WebSocket.OPEN) ws.close(); else ws.onopen = () => ws.close(); }
      } catch {}
    };
  }, [qc]);

  const addCard = async (t) => {
    try {
      const { data } = await api.post("/cards", {
        type: t.type,
        x: (window.innerWidth / 2 - view.x) / view.zoom - 160 + Math.random() * 80 - 40,
        y: (window.innerHeight / 2 - view.y) / view.zoom - 140 + Math.random() * 80 - 40,
        w: t.w, color: t.color, data: {},
      });
      qc.setQueryData(["cards"], (old = []) => [...old, data]);
      toast.success(`${t.label} card added ✦`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const updateCard = useCallback(async (cid, patch, optimistic = true) => {
    if (optimistic) {
      qc.setQueryData(["cards"], (old = []) => old.map((c) => (c._id === cid ? { ...c, ...patch, data: { ...c.data, ...(patch.data || {}) } } : c)));
    }
    try {
      const { data } = await api.patch(`/cards/${cid}`, patch);
      qc.setQueryData(["cards"], (old = []) => old.map((c) => (c._id === cid ? data : c)));
      return data;
    } catch (e) {
      toast.error(apiError(e));
      qc.invalidateQueries({ queryKey: ["cards"] });
      throw e;
    }
  }, [qc]);

  const deleteCard = useCallback(async (cid) => {
    qc.setQueryData(["cards"], (old = []) => old.filter((c) => c._id !== cid));
    qc.setQueryData(["connectors"], (old = []) => old.filter((cn) => cn.from_card !== cid && cn.to_card !== cid));
    try { await api.delete(`/cards/${cid}`); } catch (e) { toast.error(apiError(e)); qc.invalidateQueries({ queryKey: ["cards"] }); }
  }, [qc]);

  const registerRef = useCallback((cid, el) => { cardRefs.current[cid] = el; }, []);
  const onDragMove = useCallback((cid, p) => {
    setLivePos((lp) => {
      if (p === null) { const { [cid]: _, ...rest } = lp; return rest; }
      return { ...lp, [cid]: p };
    });
  }, []);

  const onConnectClick = async (cid) => {
    if (!connect.source) { setConnect({ active: true, source: cid }); return; }
    if (connect.source === cid) { setConnect({ active: false, source: null }); return; }
    try {
      const { data } = await api.post("/connectors", { from_card: connect.source, to_card: cid });
      qc.setQueryData(["connectors"], (old = []) => old.some((c) => c._id === data._id) ? old : [...old, data]);
      toast.success("Cards linked ✦");
    } catch (e) { toast.error(apiError(e)); }
    setConnect({ active: false, source: null });
  };

  const deleteConnector = async (cnid) => {
    qc.setQueryData(["connectors"], (old = []) => old.filter((c) => c._id !== cnid));
    try { await api.delete(`/connectors/${cnid}`); } catch (e) { toast.error(apiError(e)); }
  };

  const anchor = (cid) => {
    const c = cards.find((k) => k._id === cid);
    if (!c) return null;
    const lp = livePos[cid];
    const hh = cardRefs.current[cid]?.offsetHeight || c.h || 160;
    return { x: (lp?.x ?? c.x) + c.w / 2, y: (lp?.y ?? c.y) + hh / 2 };
  };

  const onCanvasPointerDown = (e) => {
    if (e.target !== e.currentTarget) return;
    panRef.current = { sx: e.clientX, sy: e.clientY, ox: view.x, oy: view.y };
    const move = (ev) => setView((v) => ({ ...v, x: panRef.current.ox + ev.clientX - panRef.current.sx, y: panRef.current.oy + ev.clientY - panRef.current.sy }));
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const visible = cards.filter((c) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return JSON.stringify(c.data).toLowerCase().includes(s) || c.type.includes(s);
  });

  const firstName = (user?.name || "").split(" ")[0] || "there";

  return (
    <div className="relative h-screen w-screen overflow-hidden bg-background" data-testid="dashboard-page"
      style={currentVibe.bg ? { background: currentVibe.bg } : undefined}>
      {/* Canvas */}
      {mode === "canvas" ? (
      <div className="absolute inset-0 canvas-dots" onPointerDown={onCanvasPointerDown} data-testid="canvas-area"
        style={{ touchAction: "none", ...(currentVibe.dot ? { "--vibe-dot": currentVibe.dot } : {}) }}>
        <div style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.zoom})`, transformOrigin: "0 0" }} className="absolute">
          <svg className="pointer-events-none absolute overflow-visible" width="1" height="1" style={{ left: 0, top: 0 }} data-testid="connectors-layer">
            <defs>
              <marker id="arrowhead" markerWidth="9" markerHeight="9" refX="7" refY="3.5" orient="auto">
                <path d="M0,0 L7,3.5 L0,7" fill="none" stroke="#8B5CF6" strokeWidth="1.8" />
              </marker>
            </defs>
            {connectors.map((cn) => {
              const a = anchor(cn.from_card);
              const b = anchor(cn.to_card);
              if (!a || !b) return null;
              const dx = Math.abs(b.x - a.x) * 0.35 + 40;
              const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
              return (
                <g key={cn._id}>
                  <path d={`M ${a.x} ${a.y} C ${a.x + dx} ${a.y}, ${b.x - dx} ${b.y}, ${b.x} ${b.y}`}
                    fill="none" stroke="#8B5CF6" strokeWidth="2.5" strokeDasharray="7 5" opacity="0.75" markerEnd="url(#arrowhead)" />
                  <g className="pointer-events-auto cursor-pointer">
                    <circle data-testid={`connector-delete-${cn._id}`} cx={mid.x} cy={mid.y} r="14" fill="#8B5CF6" opacity="0.001"
                      onClick={() => deleteConnector(cn._id)} style={{ cursor: "pointer" }} />
                    <circle cx={mid.x} cy={mid.y} r="9" fill="#8B5CF6" opacity="0.9" pointerEvents="none" />
                    <path d={`M ${mid.x - 3.5} ${mid.y - 3.5} L ${mid.x + 3.5} ${mid.y + 3.5} M ${mid.x + 3.5} ${mid.y - 3.5} L ${mid.x - 3.5} ${mid.y + 3.5}`}
                      stroke="white" strokeWidth="1.8" pointerEvents="none" />
                  </g>
                </g>
              );
            })}
          </svg>
          <div className="pointer-events-none absolute -top-2 left-4 select-none font-hand text-3xl text-violet-400/70 rotate-[-4deg]">
            {greeting}
          </div>
          {visible.map((card) => (
            <CardShell key={card._id} card={card} zoom={view.zoom} dark={dark}
              users={users} me={user} online={online} onUpdate={updateCard} onDelete={deleteCard}
              connectMode={connect.active} connectSource={connect.source}
              onConnectClick={onConnectClick} onDragMove={onDragMove} registerRef={registerRef} />
          ))}
          {cards.length === 0 && (
            <div className="pointer-events-none absolute left-24 top-40 w-96 select-none">
              <p className="font-hand text-4xl text-pink-400 rotate-[-3deg]">this canvas is all yours ✦</p>
              <p className="mt-2 font-hand text-2xl text-violet-400 rotate-[2deg]">hit "Add Card" up top to start →</p>
            </div>
          )}
        </div>
      </div>
      ) : (
      <div className="absolute inset-0 overflow-y-auto canvas-dots px-6 pb-8 pt-24" data-testid="grid-view"
        style={currentVibe.dot ? { "--vibe-dot": currentVibe.dot } : undefined}>
        <div className="mx-auto max-w-[1600px] columns-1 gap-4 sm:columns-2 lg:columns-3 2xl:columns-4">
          {visible.map((card) => (
            <CardShell key={card._id} card={card} zoom={1} dark={dark} gridMode
              users={users} me={user} online={online} onUpdate={updateCard} onDelete={deleteCard} />
          ))}
        </div>
        {cards.length === 0 && (
          <p className="mt-24 text-center font-hand text-3xl text-pink-400">nothing here yet — add a card ✦</p>
        )}
      </div>
      )}

      {connect.active && (
        <div className="absolute left-1/2 top-20 z-40 flex -translate-x-1/2 items-center gap-2 rounded-full border border-violet-400/40 bg-card/90 px-4 py-2 text-sm shadow-xl backdrop-blur-xl"
          data-testid="connect-mode-banner">
          <Spline className="h-4 w-4 text-violet-500" />
          {connect.source ? "Now click the card to link to →" : "Click the first card to link ✦"}
          <button data-testid="connect-cancel-button" onClick={() => setConnect({ active: false, source: null })}
            className="rounded-full p-0.5 hover:bg-muted"><X className="h-3.5 w-3.5" /></button>
        </div>
      )}

      {/* Top navbar */}
      <header data-testid="top-navbar"
        className="absolute left-1/2 top-4 z-40 flex w-[min(96vw,1100px)] -translate-x-1/2 items-center gap-3 rounded-2xl border border-border bg-card/80 px-4 py-2.5 shadow-2xl backdrop-blur-xl">
        <div className="flex items-center gap-1.5 font-display text-xl font-extrabold tracking-tight">
          <Sparkles className={`h-5 w-5 text-violet-500 ${pinging ? "ping-pulse rounded-full" : ""}`} />
          <span className="bg-gradient-to-r from-violet-500 to-pink-500 bg-clip-text text-transparent">Nexfy</span>
        </div>
        <div className="hidden text-sm text-muted-foreground sm:block">
          hey <span className="font-semibold text-foreground">{firstName}</span> 👋
        </div>
        <div className="relative ml-auto flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input data-testid="search-input" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search cards..."
            className="w-full rounded-xl border border-border bg-secondary py-2 pl-9 pr-3 text-sm outline-none focus:border-violet-400 transition-colors" />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="add-card-button"
              className="flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-violet-500 to-pink-500 px-4 py-2 text-sm font-bold text-white shadow-lg shadow-violet-500/25 transition-transform hover:scale-105 active:scale-95">
              <Plus className="h-4 w-4" /> Add Card
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48 rounded-xl border-border bg-popover/95 backdrop-blur-xl">
            {CARD_TYPES.map((t) => (
              <DropdownMenuItem key={t.type} data-testid={`add-card-${t.type}`} onClick={() => addCard(t)} className="gap-2 rounded-lg cursor-pointer">
                <t.icon className="h-4 w-4 text-violet-500" /> {t.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <button data-testid="view-toggle" onClick={() => { setMode(mode === "canvas" ? "grid" : "canvas"); setConnect({ active: false, source: null }); }}
          className="rounded-xl border border-border bg-secondary p-2 transition-colors hover:bg-muted"
          title={mode === "canvas" ? "Switch to grid view" : "Switch to canvas view"}>
          {mode === "canvas" ? <LayoutGrid className="h-4 w-4" /> : <Shapes className="h-4 w-4" />}
        </button>
        <button data-testid="theme-toggle" onClick={() => setDark(!dark)}
          className="rounded-xl border border-border bg-secondary p-2 transition-colors hover:bg-muted">
          {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </button>
        {user?.role === "admin" && (
          <button data-testid="admin-panel-button" onClick={() => setAdminOpen(true)}
            className="rounded-xl border border-border bg-secondary p-2 transition-colors hover:bg-muted" title="Manage users">
            <Users className="h-4 w-4" />
          </button>
        )}
        <button data-testid="logout-button" onClick={logout}
          className="rounded-xl border border-border bg-secondary p-2 text-red-500 transition-colors hover:bg-red-500/10" title="Log out">
          <LogOut className="h-4 w-4" />
        </button>
      </header>

      {/* Canvas controls */}
      {mode === "canvas" && (
      <div data-testid="canvas-controls"
        className="absolute bottom-5 left-5 z-40 flex items-center gap-1 rounded-2xl border border-border bg-card/80 p-1.5 shadow-xl backdrop-blur-xl">
        <button data-testid="zoom-out-button" onClick={() => setView((v) => ({ ...v, zoom: Math.max(0.4, +(v.zoom - 0.1).toFixed(2)) }))}
          className="rounded-xl p-2 transition-colors hover:bg-muted"><ZoomOut className="h-4 w-4" /></button>
        <span className="w-12 text-center font-mono text-xs text-muted-foreground">{Math.round(view.zoom * 100)}%</span>
        <button data-testid="zoom-in-button" onClick={() => setView((v) => ({ ...v, zoom: Math.min(2, +(v.zoom + 0.1).toFixed(2)) }))}
          className="rounded-xl p-2 transition-colors hover:bg-muted"><ZoomIn className="h-4 w-4" /></button>
        <button data-testid="reset-view-button" onClick={() => setView({ x: 0, y: 0, zoom: 1 })}
          className="rounded-xl p-2 transition-colors hover:bg-muted" title="Reset view"><Maximize className="h-4 w-4" /></button>
        <button data-testid="connect-mode-button"
          onClick={() => setConnect((c) => c.active ? { active: false, source: null } : { active: true, source: null })}
          className={`rounded-xl p-2 transition-colors ${connect.active ? "bg-violet-500 text-white" : "hover:bg-muted"}`}
          title="Link cards with an arrow"><Spline className="h-4 w-4" /></button>
      </div>
      )}

      {mode === "canvas" && (
      <div className="pointer-events-none absolute bottom-6 right-20 z-30 select-none font-hand text-2xl text-violet-400/60 rotate-[3deg]">
        drag cards anywhere ✦ pan the space
      </div>
      )}

      {/* Vibe picker */}
      {vibeOpen && (
        <div className="absolute bottom-20 right-5 z-50 w-72 rounded-2xl border border-border bg-card/95 p-3 shadow-2xl backdrop-blur-xl card-pop" data-testid="vibe-panel">
          <div className="mb-2 flex items-center justify-between">
            <span className="font-display text-sm font-bold">Artboard Vibes ✦</span>
            <button data-testid="vibe-reset" onClick={() => applyVibe(VIBES[0])}
              className="text-xs text-violet-500 hover:underline">Reset</button>
          </div>
          <div className="max-h-80 space-y-2.5 overflow-y-auto pr-1">
            {MOODS.map((mood) => (
              <div key={mood}>
                <div className="mb-1 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{mood}</div>
                <div className="grid grid-cols-3 gap-1.5">
                  {VIBES.filter((v) => v.mood === mood).map((v) => (
                    <button key={v.id} data-testid={`vibe-option-${v.id}`} onClick={() => applyVibe(v)} title={v.name}
                      className={`flex flex-col items-center gap-1 rounded-xl border p-1.5 text-[10px] transition-transform hover:scale-105 ${
                        vibe === v.id ? "border-violet-500 ring-2 ring-violet-400/50" : "border-border"}`}>
                      <span className="h-6 w-full rounded-lg border border-black/10" style={{ background: v.bg }} />
                      <span className="w-full truncate text-center">{v.emoji} {v.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <button data-testid="vibe-button" onClick={() => setVibeOpen(!vibeOpen)} title="Change the vibe"
        className={`absolute bottom-5 right-5 z-50 rounded-2xl border border-border bg-card/80 p-2.5 shadow-xl backdrop-blur-xl transition-transform hover:scale-110 active:scale-95 ${vibeOpen ? "ring-2 ring-violet-400" : ""}`}>
        <Wand2 className="h-5 w-5 text-violet-500" />
      </button>

      <AdminPanel open={adminOpen} onClose={() => setAdminOpen(false)} users={users} online={online} />
      {templateOpen && <TemplatePicker onDone={finishTemplate} />}
      {!templateOpen && tourOpen && mode === "canvas" && <WelcomeTour onClose={closeTour} />}
    </div>
  );
}
